// groupItemWide/GroupItemWide.tsx
import React from "react";
import { Link } from "react-router-dom";
import {
  MdFavorite,
  MdFavoriteBorder,
  MdVisibility,
  MdStar,
  MdCategory,
  MdTune,
} from "react-icons/md";
import { useSelector } from "react-redux";
import { GroupedProduct } from "../../../../../common/types/types";

import { selectCurrentUser } from "../../../../../store/slices/currentUserSlice";
import { selectCurrentCluster } from "../../../../../store/slices/currentClusterSlice";
import { useGroupItem } from "../useGroupItem";
import { useCartActions } from "../../../../../common/hooks/useCartActions";
import { useTheme } from "../../../../../common/hooks/useTheme";

import styles from "./GroupItemWide.module.scss";
import { getImageUri } from "../../../../../common/utils/functions/getImageUri";
import { StockBadge } from "../../../../../common/components/stockBage/StockBage";
import { shortenText } from "../../../../../common/utils/functions/shortenText";
import { ROUTES } from "../../../../../common/utils/constants";
import { formatPriceToLocale } from "../../../../../common/utils/functions/formatPriceToLocale";
import { GroupCardButton } from "../../../../../common/components/groupCardButton/GroupCardButton";

type Props = {
  group: GroupedProduct;
  onPress: () => void;
  getCategoryName: (id: number) => string | undefined;
  isAuth?: boolean;
  usingStock?: boolean;
};

export const GroupItemWide = ({
  group,
  onPress,
  getCategoryName,
  isAuth,
  usingStock,
}: Props) => {
  const { theme } = useTheme();
  const currentUser = useSelector(selectCurrentUser);
  const currentCluster = useSelector(selectCurrentCluster);
  const forceStore = currentCluster?.forceStore ?? false;

  const {
    minPrice,
    variantsCount,
    firstImage,
    minRating,
    totalFavorites,
    totalViews,
  } = useGroupItem(group);

  const firstVariant = group.variants?.[0];
  const { showComplexButton } = useCartActions(
    (firstVariant || { id: 0, name: "", prices: [], files: [] }) as any,
  );

  const handleFavoritePress = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const hasStockData = group.variants.some(
    (v) => v.useStock && v.stock_stores && v.stock_stores.length > 0,
  );

  const allItemStops = group.variants.flatMap((v) => v.item_stops || []);
  const showStatsOnImage = totalFavorites > 0 || totalViews.length > 0;

  return (
    <Link
      to={`${ROUTES.GROUP}/${group.groupId}`}
      className={styles.container}
    >
      <div className={styles.imageContainer}>
        <img
          className={styles.image}
          src={`${getImageUri()}${firstImage}`}
          alt={group.name}
        />

        {group.selected && currentUser && (
          <button
            className={styles.favoriteButton}
            onClick={handleFavoritePress}
            aria-label={
              group.selected.length > 0
                ? "Удалить из избранного"
                : "Добавить в избранное"
            }
          >
            {group.selected.length > 0 ? (
              <MdFavorite size={16} color="#fff" />
            ) : (
              <MdFavoriteBorder size={16} color="#fff" />
            )}
          </button>
        )}

        <div
          className={styles.variantsBadge}
          style={{ backgroundColor: theme.primary + "CC" }}
        >
          <span className={styles.variantsBadgeText}>{variantsCount} вар.</span>
        </div>

        {showStatsOnImage && (
          <div className={styles.statsOverlay}>
            {totalFavorites > 0 && (
              <div className={styles.overlayStatItem}>
                <MdFavoriteBorder size={12} color="#fff" />
                <span className={styles.overlayStatText}>{totalFavorites}</span>
              </div>
            )}
            {totalViews.length > 0 && (
              <div className={styles.overlayStatItem}>
                <MdVisibility size={12} color="#fff" />
                <span className={styles.overlayStatText}>
                  {totalViews.length}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className={styles.body}>
        <span className={styles.nameText} title={group.name}>
          {group.name}
        </span>

        <span className={styles.priceText}>
          {minPrice > 0
            ? `от ${formatPriceToLocale(minPrice)} ₽`
            : showComplexButton
              ? ""
              : "Нет цены"}
        </span>

        {usingStock && hasStockData && (
          <div className={styles.stockBadgeContainer}>
            <StockBadge
              stockStores={group.variants.flatMap((v) => v.stock_stores || [])}
              stockEnabled={true}
              forceStore={forceStore}
              itemStops={allItemStops}
            />
          </div>
        )}

        <div className={styles.metaRow}>
          <MdCategory size={12} color={theme.textSecondary} />
          <span className={styles.category}>
            {shortenText(getCategoryName(Number(group.categoryId)), 15)}
          </span>

          {minRating !== undefined && (
            <div className={styles.ratingContainer}>
              <MdStar size={12} color="#FFD700" />
              <span className={styles.ratingText}>
                от {minRating.toFixed(1)}
              </span>
            </div>
          )}
        </div>

        <div className={styles.buttonContainer}>
        <GroupCardButton onPress={onPress} />
        </div>
      </div>
    </Link>
  );
};
